/**
 * Contestar, devolver o cerrar.  (spec 069, spec 074)
 *
 * Escribir solo cuando el equipo lleva el hilo. Las tres acciones
 * piden confirmación: cada una dice qué pasa si se confirma.
 */
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'

import { useSoloLectura } from '../auth'
import { api, ErrorApi } from '../lib/api'
import { CONFIRMAR_MANDO, marcaDe, tonoDeAccion, type AccionMando, type Mando } from '../lib/mando-hilo'
import { Button } from './ui/button'

export type { Mando }

export function ControlDelHilo({
    accountId,
    conversationId,
    mando,
    onHecho,
}: {
    accountId: number
    conversationId: number
    mando: Mando | null
    onHecho?: () => void
}) {
    const soloLectura = useSoloLectura()
    const [texto, setTexto] = useState('')
    const [aviso, setAviso] = useState<string | null>(null)
    const [confirmar, setConfirmar] = useState<AccionMando | null>(null)

    const control = useMutation({
        mutationFn: (accion: AccionMando) =>
            api.post<{ ok: true }>(`/business/${accountId}/conversations/${conversationId}/control`, { accion }),
        onSuccess: (_d, accion) => {
            setConfirmar(null)
            setAviso(
                accion === 'tomar'
                    ? 'Ahora contestas tú. Agendify no escribe en este hilo.'
                    : accion === 'devolver'
                      ? 'Devuelto. Agendify contesta a partir del siguiente mensaje.'
                      : 'Cerrado. Nadie contesta hasta que lo reabras.',
            )
            onHecho?.()
        },
        onError: () => setConfirmar(null),
    })

    const envio = useMutation({
        mutationFn: () =>
            api.post<{ ok: true }>(`/business/${accountId}/conversations/${conversationId}/mensaje`, { texto }),
        onSuccess: () => {
            setTexto('')
            setAviso(null)
            onHecho?.()
        },
    })

    if (soloLectura) return null

    const puedeEscribir = mando === 'equipo'
    const error =
        (control.error instanceof ErrorApi ? control.error.message : control.isError ? 'No se pudo cambiar el hilo.' : null) ??
        (envio.error instanceof ErrorApi ? envio.error.message : envio.isError ? 'No se pudo enviar.' : null)
    const dialogo = confirmar ? CONFIRMAR_MANDO[confirmar] : null
    const marca = marcaDe(mando)

    return (
        <div className="relative mt-3 space-y-2 border-t border-neutral-800 pt-3">
            <div className="flex items-start gap-2">
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${marca.punto}`} aria-hidden />
                <p>
                    <span className={`text-sm font-medium ${marca.texto}`}>{marca.nombre}</span>
                    <span className="mt-0.5 block text-xs text-neutral-500 light:text-neutral-600">{marca.detalle}</span>
                </p>
            </div>
            <div className="flex flex-wrap gap-2">
                <Button
                    type="button"
                    className={tonoDeAccion('tomar')}
                    disabled={control.isPending || (mando !== 'agendify' && mando !== 'cerrada')}
                    onClick={() => setConfirmar('tomar')}
                >
                    Contesto yo
                </Button>
                <Button
                    type="button"
                    className={tonoDeAccion('devolver')}
                    disabled={control.isPending || (mando !== 'equipo' && mando !== 'cerrada')}
                    onClick={() => setConfirmar('devolver')}
                >
                    Devolver a Agendify
                </Button>
                <Button
                    type="button"
                    className={tonoDeAccion('cerrar')}
                    disabled={control.isPending || (mando !== 'agendify' && mando !== 'equipo')}
                    onClick={() => setConfirmar('cerrar')}
                >
                    Cerrar
                </Button>
            </div>
            <form
                className="flex items-stretch gap-2"
                onSubmit={(e) => {
                    e.preventDefault()
                    if (!puedeEscribir || texto.trim() === '' || envio.isPending) return
                    envio.mutate()
                }}
            >
                <textarea
                    value={texto}
                    onChange={(e) => setTexto(e.target.value)}
                    onKeyDown={(e) => {
                        if (!puedeEscribir) return
                        if (e.nativeEvent.isComposing) return
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            if (texto.trim() === '' || envio.isPending) return
                            envio.mutate()
                        }
                    }}
                    rows={2}
                    maxLength={4000}
                    disabled={!puedeEscribir}
                    placeholder={
                        puedeEscribir
                            ? 'Escribe al cliente. Enter envía.'
                            : 'Para escribir, contesta tú.'
                    }
                    className="min-h-[3.5rem] w-full resize-none rounded-lg border border-neutral-800 bg-neutral-950 px-3 py-2 text-sm text-neutral-100 placeholder:text-neutral-600 focus:border-sky-700 focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
                <Button
                    type="submit"
                    className="h-auto shrink-0 px-4"
                    disabled={!puedeEscribir || texto.trim() === '' || envio.isPending}
                >
                    {envio.isPending ? 'Enviando…' : 'Enviar'}
                </Button>
            </form>
            {aviso && <p className="text-xs text-emerald-300">{aviso}</p>}
            {error && <p className="text-sm text-rose-400">{error}</p>}
            {dialogo && confirmar && (
                <div
                    className="absolute inset-x-0 bottom-0 z-10 rounded-lg border border-neutral-600 bg-neutral-950 p-3 shadow-xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="confirmar-mando"
                >
                    <h4 id="confirmar-mando" className="text-sm font-semibold text-neutral-100">
                        {dialogo.titulo}
                    </h4>
                    <p className="mt-1 text-xs text-neutral-400">{dialogo.cuerpo}</p>
                    <div className="mt-3 flex justify-end gap-2">
                        <Button type="button" disabled={control.isPending} onClick={() => setConfirmar(null)}>
                            Cancelar
                        </Button>
                        <Button
                            type="button"
                            className={tonoDeAccion(confirmar)}
                            disabled={control.isPending}
                            onClick={() => control.mutate(confirmar)}
                        >
                            {control.isPending ? 'Cambiando…' : dialogo.boton}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
